// src/pages/DashboardChequesECheq.jsx
import React, { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import moment from "moment";

import { getCartera } from "../helpers/rutaCarteras";

const fmtARS = (n) =>
  Number(n || 0).toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const money = (n) => `$ ${fmtARS(n)}`;

const estadoUi = (v) => {
  const s = String(v || "").toUpperCase().trim();

  // Para el tablero lo tratamos como obligación futura
  if (s === "ENDOSADO_A_INGRESAR") return "EMITIDO_A_INGRESAR";

  return s;
};

const tipoUi = (v) => String(v || "").toUpperCase().trim();

const getNro = (r) => r?.nro || r?.nroCheque || r?.idICheq || "";
const getBanco = (r) => r?.bancoEmisor || r?.banco || "";

const getFechaCheque = (r) => r?.fechaCheque || r?.fechaPagoDiferido || "";

const getFechaPago = (r) =>
  r?.fechaPago || r?.fechaLiquidacion || r?.fechaPagoDiferido || "";

const getCobradoA = (r) => r?.cobradoA || r?.librador || r?.emisor || "";
const getPagadoA = (r) => r?.pagadoA || r?.beneficiario || "";

const getCliente = (r) =>
  r?.clienteId?.razonsocial || r?.clienteId?.nombre || "";

const getOrigen = (r) => {
  if (r?.ocId?.numero) return `OC ${r.ocId.numero}`;
  if (r?.ocId?.nroOrdenCliente) return `OC ${r.ocId.nroOrdenCliente}`;

  if (r?.opId?.numero) return `OP ${r.opId.numero}`;
  if (r?.opId?.nroOrdenPago) return `OP ${r.opId.nroOrdenPago}`;

  if (r?.origenTipo) return r.origenTipo;

  return "";
};

const fechaLarga = (v) => {
  if (!v) return "";
  const m = moment(v);
  return m.isValid() ? m.format("DD/MM/YYYY") : "";
};

const sumImporte = (arr) =>
  arr.reduce((acc, x) => acc + Number(x?.importe || 0), 0);

const isEnCartera = (r) => estadoUi(r.estado) === "EN_CARTERA";

const isEmitido = (r) =>
  ["EMITIDO", "EMITIDO_A_INGRESAR"].includes(estadoUi(r.estado));

const isAnulado = (r) => estadoUi(r.estado) === "ANULADO";

const diasHastaVencimiento = (r) => {
  const f = getFechaPago(r) || getFechaCheque(r);
  if (!f) return null;

  const hoy = moment().startOf("day");
  const venc = moment(f).startOf("day");

  if (!venc.isValid()) return null;

  return venc.diff(hoy, "days");
};

const dentroDeProximosDias = (r, dias) => {
  const d = diasHastaVencimiento(r);
  return d !== null && d >= 0 && d <= Number(dias || 7);
};

const labelDias = (d) => {
  if (d === null) return "";
  if (d === 0) return "HOY";
  if (d === 1) return "MAÑANA";
  return `${d} días`;
};

const sortByVencimiento = (a, b) => {
  const fa = getFechaPago(a) || getFechaCheque(a) || a?.recibidoEn;
  const fb = getFechaPago(b) || getFechaCheque(b) || b?.recibidoEn;

  return moment(fa || 0).valueOf() - moment(fb || 0).valueOf();
};

function KpiCard({
  titulo,
  valor,
  subtitulo,
  color = "#2563eb",
  tipo = "money",
}) {
  const mostrar =
    tipo === "veces"
      ? `${Number(valor || 0).toLocaleString("es-AR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })} veces`
      : tipo === "porcentaje"
      ? `${Number(valor || 0).toLocaleString("es-AR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}%`
      : money(valor);

  return (
    <Card
      className="shadow-sm h-100"
      style={{
        background: "#ffffff",
        color: "#1f2937",
        border: "1px solid #dbe3ef",
        borderLeft: `5px solid ${color}`,
        borderRadius: 12,
      }}
    >
      <Card.Body>
        <div
          style={{
            fontSize: 12,
            color: "#6b7280",
            textTransform: "uppercase",
            fontWeight: 700,
          }}
        >
          {titulo}
        </div>

        <div style={{ fontSize: 28, fontWeight: 800, color }}>{mostrar}</div>

        <div style={{ fontSize: 12, color: "#6b7280" }}>{subtitulo}</div>
      </Card.Body>
    </Card>
  );
}

function BarraHorizontal({ label, cobrar, pagar, max }) {
  const cobrarPct =
    max > 0 && cobrar > 0 ? Math.max((cobrar / max) * 100, 2) : 0;
  const pagarPct =
    max > 0 && pagar > 0 ? Math.max((pagar / max) * 100, 2) : 0;

  return (
    <div style={{ marginBottom: 15 }}>
      <div
        style={{
          color: "#1f2937",
          fontSize: 13,
          fontWeight: 700,
          marginBottom: 5,
        }}
      >
        {label}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ width: 70, color: "#16a34a", fontSize: 11 }}>
          Cobrar
        </div>

        <div
          style={{
            flex: 1,
            background: "#e5e7eb",
            borderRadius: 8,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${cobrarPct}%`,
              background: "#22c55e",
              height: 12,
              borderRadius: 8,
            }}
          />
        </div>

        <div
          style={{
            width: 120,
            color: "#1f2937",
            fontSize: 11,
            textAlign: "right",
            fontWeight: 600,
          }}
        >
          {money(cobrar)}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginTop: 4,
        }}
      >
        <div style={{ width: 70, color: "#dc2626", fontSize: 11 }}>Pagar</div>

        <div
          style={{
            flex: 1,
            background: "#e5e7eb",
            borderRadius: 8,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${pagarPct}%`,
              background: "#ef4444",
              height: 12,
              borderRadius: 8,
            }}
          />
        </div>

        <div
          style={{
            width: 120,
            color: "#1f2937",
            fontSize: 11,
            textAlign: "right",
            fontWeight: 600,
          }}
        >
          {money(pagar)}
        </div>
      </div>
    </div>
  );
}

function MiniCobertura({ cobrar, pagar }) {
  const total = Number(cobrar || 0) + Number(pagar || 0);
  const cobrarPct = total > 0 ? (Number(cobrar || 0) / total) * 100 : 0;
  const pagarPct = total > 0 ? (Number(pagar || 0) / total) * 100 : 0;

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <div
          style={{
            color: "#16a34a",
            fontSize: 13,
            marginBottom: 4,
            fontWeight: 600,
          }}
        >
          A cobrar {cobrarPct.toFixed(1)}%
        </div>

        <div
          style={{
            background: "#e5e7eb",
            height: 18,
            borderRadius: 10,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${cobrarPct}%`,
              height: "100%",
              background: "#22c55e",
            }}
          />
        </div>
      </div>

      <div>
        <div
          style={{
            color: "#dc2626",
            fontSize: 13,
            marginBottom: 4,
            fontWeight: 600,
          }}
        >
          A pagar {pagarPct.toFixed(1)}%
        </div>

        <div
          style={{
            background: "#e5e7eb",
            height: 18,
            borderRadius: 10,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${pagarPct}%`,
              height: "100%",
              background: "#ef4444",
            }}
          />
        </div>
      </div>

      <div style={{ marginTop: 18, fontSize: 13, color: "#1f2937" }}>
        <div>
          Cobrar: <b>{money(cobrar)}</b>
        </div>
        <div>
          Pagar: <b>{money(pagar)}</b>
        </div>
      </div>
    </div>
  );
}

const cardWhiteStyle = {
  background: "#ffffff",
  color: "#1f2937",
  border: "1px solid #dbe3ef",
  borderRadius: 12,
};

export default function DashboardChequesECheq() {
  const [data, setData] = useState([]);
  const [dias, setDias] = useState(10);
  const [tipoFiltro, setTipoFiltro] = useState("");
  const [grupoFiltro, setGrupoFiltro] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const consultar = async () => {
    setErr("");
    setLoading(true);

    try {
      const resp = await getCartera();

      const items = Array.isArray(resp)
        ? resp
        : Array.isArray(resp?.items)
        ? resp.items
        : Array.isArray(resp?.elementos)
        ? resp.elementos
        : [];

      setData(
        items.map((x) => ({
          ...x,
          estado: estadoUi(x.estado),
          tipo: tipoUi(x.tipo),
        }))
      );
    } catch (e) {
      setData([]);
      setErr(e?.message || "No se pudo cargar el dashboard.");
    } finally {
      setLoading(false);
    }
  };

  const baseFiltrada = useMemo(() => {
    return data
      .filter((x) => !isAnulado(x))
      .filter((x) => dentroDeProximosDias(x, dias))
      .filter((x) => {
        if (tipoFiltro && tipoUi(x.tipo) !== tipoFiltro) return false;
        if (grupoFiltro === "COBRAR" && !isEnCartera(x)) return false;
        if (grupoFiltro === "PAGAR" && !isEmitido(x)) return false;
        return true;
      })
      .sort(sortByVencimiento);
  }, [data, dias, tipoFiltro, grupoFiltro]);

  const aCobrar = useMemo(
    () => baseFiltrada.filter(isEnCartera).sort(sortByVencimiento),
    [baseFiltrada]
  );

  const aPagar = useMemo(
    () => baseFiltrada.filter(isEmitido).sort(sortByVencimiento),
    [baseFiltrada]
  );

  const totalACobrar = sumImporte(aCobrar);
  const totalAPagar = sumImporte(aPagar);
  const diferencia = totalACobrar - totalAPagar;

  const coberturaPorcentaje =
    totalAPagar > 0 ? (totalACobrar / totalAPagar) * 100 : 0;

  const coberturaVeces = totalAPagar > 0 ? totalACobrar / totalAPagar : 0;

  const seriePorDia = useMemo(() => {
    const mapa = new Map();

    baseFiltrada.forEach((r) => {
      const f = getFechaPago(r) || getFechaCheque(r);
      const m = moment(f);
      const key = m.isValid() ? m.format("YYYY-MM-DD") : "-";
      const label = m.isValid() ? m.format("DD/MM") : "-";

      if (!mapa.has(key)) {
        mapa.set(key, {
          key,
          fecha: label,
          cobrar: 0,
          pagar: 0,
          saldo: 0,
        });
      }

      const item = mapa.get(key);

      if (isEnCartera(r)) item.cobrar += Number(r.importe || 0);
      if (isEmitido(r)) item.pagar += Number(r.importe || 0);

      item.saldo = item.cobrar - item.pagar;
    });

    return Array.from(mapa.values()).sort((a, b) => {
      if (a.key === "-") return 1;
      if (b.key === "-") return -1;
      return moment(a.key).valueOf() - moment(b.key).valueOf();
    });
  }, [baseFiltrada]);

  const barrasPorTipo = useMemo(() => {
    const chequeCobrar = aCobrar
      .filter((x) => tipoUi(x.tipo) === "CHEQUE")
      .reduce((a, x) => a + Number(x.importe || 0), 0);

    const echeqCobrar = aCobrar
      .filter((x) => tipoUi(x.tipo) === "ICHEQ")
      .reduce((a, x) => a + Number(x.importe || 0), 0);

    const chequePagar = aPagar
      .filter((x) => tipoUi(x.tipo) === "CHEQUE")
      .reduce((a, x) => a + Number(x.importe || 0), 0);

    const echeqPagar = aPagar
      .filter((x) => tipoUi(x.tipo) === "ICHEQ")
      .reduce((a, x) => a + Number(x.importe || 0), 0);

    return [
      { tipo: "CHEQUE", cobrar: chequeCobrar, pagar: chequePagar },
      { tipo: "ECHEQ", cobrar: echeqCobrar, pagar: echeqPagar },
    ];
  }, [aCobrar, aPagar]);

  const topVencimientos = useMemo(() => {
    return [...baseFiltrada].sort(sortByVencimiento).slice(0, 10);
  }, [baseFiltrada]);

  const maxSerie = useMemo(() => {
    return Math.max(...seriePorDia.map((x) => Math.max(x.cobrar, x.pagar)), 1);
  }, [seriePorDia]);

  const maxTipo = useMemo(() => {
    return Math.max(
      ...barrasPorTipo.map((x) => Math.max(x.cobrar, x.pagar)),
      1
    );
  }, [barrasPorTipo]);

  const limpiar = () => {
    setDias(10);
    setTipoFiltro("");
    setGrupoFiltro("");
  };

  return (
    <Container
      fluid
      className="py-3"
      style={{ background: "#ffffff", minHeight: "100vh" }}
    >
      <div
        className="d-flex flex-wrap justify-content-between align-items-end mb-3"
        style={{ color: "#1f2937", gap: 12 }}
      >
        <div>
          <h3 className="mb-1">Dashboard Cheques / eCheq</h3>
          <small style={{ color: "#6b7280" }}>
            Cobros y obligaciones próximas por vencimiento
          </small>
        </div>

        <div className="d-flex flex-wrap" style={{ gap: 8 }}>
          <Form.Select
            size="sm"
            value={dias}
            onChange={(e) => setDias(Number(e.target.value))}
            style={{ width: 180 }}
          >
            <option value={2}>Próximos 2 días</option>
            <option value={5}>Próximos 5 días</option>
            <option value={7}>Próximos 7 días</option>
            <option value={10}>Próximos 10 días</option>
            <option value={15}>Próximos 15 días</option>
            <option value={30}>Próximos 30 días</option>
          </Form.Select>

          <Form.Select
            size="sm"
            value={grupoFiltro}
            onChange={(e) => setGrupoFiltro(e.target.value)}
            style={{ width: 170 }}
          >
            <option value="">Cobrar y pagar</option>
            <option value="COBRAR">Solo a cobrar</option>
            <option value="PAGAR">Solo a pagar</option>
          </Form.Select>

          <Form.Select
            size="sm"
            value={tipoFiltro}
            onChange={(e) => setTipoFiltro(e.target.value)}
            style={{ width: 150 }}
          >
            <option value="">Cheque + eCheq</option>
            <option value="CHEQUE">Cheque</option>
            <option value="ICHEQ">eCheq</option>
          </Form.Select>

          <Button variant="outline-secondary" size="sm" onClick={limpiar}>
            Limpiar
          </Button>

          <Button
            variant="primary"
            size="sm"
            onClick={consultar}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner size="sm" className="me-1" />
                Consultando…
              </>
            ) : (
              "Consultar"
            )}
          </Button>
        </div>
      </div>

      {err ? (
        <Alert variant="danger" className="py-2">
          {err}
        </Alert>
      ) : null}

      {data.length === 0 ? (
        <div className="text-muted mt-3">
          Tocá <b>Consultar</b> para cargar el dashboard.
        </div>
      ) : (
        <>
          <Row xs={1} md={2} xl={4} className="g-3 mb-3">
            <Col>
              <KpiCard
                titulo={`A cobrar próximos ${dias} días`}
                valor={totalACobrar}
                subtitulo={`${aCobrar.length} registros`}
                color="#22c55e"
              />
            </Col>

            <Col>
              <KpiCard
                titulo={`A pagar próximos ${dias} días`}
                valor={totalAPagar}
                subtitulo={`${aPagar.length} registros`}
                color="#ef4444"
              />
            </Col>

            <Col>
              <KpiCard
                titulo="Diferencia"
                valor={diferencia}
                subtitulo={
                  diferencia >= 0 ? "Cubre obligaciones" : "Faltante financiero"
                }
                color={diferencia >= 0 ? "#22c55e" : "#ef4444"}
              />
            </Col>

            <Col>
              <KpiCard
                titulo="Cobertura"
                valor={coberturaVeces}
                tipo="veces"
                subtitulo={`${coberturaPorcentaje.toLocaleString("es-AR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}% de cobertura`}
                color={coberturaPorcentaje >= 100 ? "#22c55e" : "#ef4444"}
              />
            </Col>
          </Row>

          <Row className="g-3 mb-3">
            <Col md={6}>
              <Card className="shadow-sm h-100" style={cardWhiteStyle}>
                <Card.Body>
                  <h6>Flujo por vencimiento</h6>

                  {seriePorDia.length === 0 ? (
                    <div style={{ color: "#6b7280" }}>
                      Sin datos para el filtro seleccionado.
                    </div>
                  ) : (
                    seriePorDia.map((r) => (
                      <BarraHorizontal
                        key={r.key}
                        label={r.fecha}
                        cobrar={r.cobrar}
                        pagar={r.pagar}
                        max={maxSerie}
                      />
                    ))
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col md={3}>
              <Card className="shadow-sm h-100" style={cardWhiteStyle}>
                <Card.Body>
                  <h6>Cheque / eCheq</h6>

                  {barrasPorTipo.map((r) => (
                    <BarraHorizontal
                      key={r.tipo}
                      label={r.tipo}
                      cobrar={r.cobrar}
                      pagar={r.pagar}
                      max={maxTipo}
                    />
                  ))}
                </Card.Body>
              </Card>
            </Col>

            <Col md={3}>
              <Card className="shadow-sm h-100" style={cardWhiteStyle}>
                <Card.Body>
                  <h6>Composición</h6>
                  <MiniCobertura cobrar={totalACobrar} pagar={totalAPagar} />
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-3">
            <Col md={5}>
              <Card className="shadow-sm" style={cardWhiteStyle}>
                <Card.Body>
                  <h6>Resumen por tipo</h6>

                  <div className="table-responsive">
                    <Table
                      size="sm"
                      hover
                      bordered
                      className="mb-0"
                      style={{ background: "#fff" }}
                    >
                      <thead
                        style={{ background: "#eef3f8", color: "#1f2937" }}
                      >
                        <tr>
                          <th>Tipo</th>
                          <th className="text-end">A cobrar</th>
                          <th className="text-end">A pagar</th>
                          <th className="text-end">Diferencia</th>
                        </tr>
                      </thead>

                      <tbody>
                        {barrasPorTipo.map((r) => (
                          <tr key={r.tipo}>
                            <td>{r.tipo}</td>
                            <td className="text-end">{money(r.cobrar)}</td>
                            <td className="text-end">{money(r.pagar)}</td>
                            <td
                              className="text-end"
                              style={{
                                color:
                                  r.cobrar - r.pagar >= 0
                                    ? "#22c55e"
                                    : "#ef4444",
                              }}
                            >
                              {money(r.cobrar - r.pagar)}
                            </td>
                          </tr>
                        ))}

                        <tr style={{ fontWeight: 700 }}>
                          <td>Total</td>
                          <td className="text-end">{money(totalACobrar)}</td>
                          <td className="text-end">{money(totalAPagar)}</td>
                          <td
                            className="text-end"
                            style={{
                              color: diferencia >= 0 ? "#22c55e" : "#ef4444",
                            }}
                          >
                            {money(diferencia)}
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={7}>
              <Card className="shadow-sm" style={cardWhiteStyle}>
                <Card.Body>
                  <h6>Próximos vencimientos</h6>

                  <div className="table-responsive">
                    <Table
                      size="sm"
                      hover
                      bordered
                      className="mb-0"
                      style={{ background: "#fff" }}
                    >
                      <thead
                        style={{ background: "#eef3f8", color: "#1f2937" }}
                      >
                        <tr>
                          <th>Días</th>
                          <th>Fecha</th>
                          <th>Tipo</th>
                          <th>Estado</th>
                          <th>Nro</th>
                          <th>Banco</th>
                          <th>Cliente / Benef.</th>
                          <th className="text-end">Importe</th>
                          <th>Origen</th>
                        </tr>
                      </thead>

                      <tbody>
                        {topVencimientos.map((r) => {
                          const d = diasHastaVencimiento(r);
                          const esPagar = isEmitido(r);

                          const persona = esPagar
                            ? getPagadoA(r)
                            : getCliente(r) || getCobradoA(r);

                          const fecha = getFechaPago(r) || getFechaCheque(r);

                          return (
                            <tr
                              key={r._id || `${getNro(r)}-${r.tipo}-${r.estado}`}
                            >
                              <td
                                style={{
                                  color: d <= 2 ? "#ef4444" : "#1f2937",
                                  fontWeight: d <= 2 ? 700 : 400,
                                }}
                              >
                                {labelDias(d)}
                              </td>

                              <td>{fechaLarga(fecha)}</td>
                              <td>{tipoUi(r.tipo)}</td>

                              <td
                                style={{
                                  color: esPagar ? "#ef4444" : "#22c55e",
                                }}
                              >
                                {estadoUi(r.estado)}
                              </td>

                              <td>{getNro(r)}</td>
                              <td>{getBanco(r)}</td>
                              <td>{persona}</td>
                              <td className="text-end">{money(r.importe)}</td>
                              <td>{getOrigen(r)}</td>
                            </tr>
                          );
                        })}

                        {topVencimientos.length === 0 ? (
                          <tr>
                            <td colSpan={9} className="text-center text-muted">
                              Sin vencimientos para el filtro seleccionado.
                            </td>
                          </tr>
                        ) : null}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
}